import React, { useState, useMemo, useEffect } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import axios from "axios";
import SiteFooter from "../components/SiteFooter";
import { CityAutocomplete } from "../components/CityAutocomplete";
import { splitPreview, PreviewHighlights } from "../components/PreviewHighlights";
import { requestPay } from "../utils/pay";
import { useLang } from "../i18n/LanguageContext";

const Global = createGlobalStyle`
  @import url('https://cdn.jsdelivr.net/gh/orioncactus/pretendard/dist/web/static/pretendard.css');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #FBF6EA; font-family: 'Pretendard', "Noto Sans KR", sans-serif; }
`;

const DancheongBar = styled.div`
  height: 6px; width: 100%;
  background: #FBF6EA;
  flex-shrink: 0;
`;

const floatHero = keyframes`
  0%,100% { transform: translateY(0); }
  50%      { transform: translateY(-12px); }
`;
const fadeIn = keyframes`from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}`;

/* ─── 공통 레이아웃 ─────────────────────────────────────────────────── */
const Wrap = styled.div`
  min-height: 100vh; display: flex; flex-direction: column; align-items: center;
  padding: 0 0 60px; background: #FBF6EA;
`;
/* 구형 Header — STEP 1에서만 사용 */
const Header = styled.div`text-align:center; margin: 0 0 20px; padding-top: 24px;`;
const Logo = styled.div`font-size:2rem; margin-bottom:6px;`;
const Title = styled.h1`font-size:1.4rem; color:#2B2A26; font-weight:900;`;
const Sub = styled.p`font-size:0.8rem; color:#8C8677; margin-top:5px;`;

/* ─── STEP 0 : 히어로 + 서비스카드 ──────────────────────────────────── */
const HookWrap = styled.div`width:100%; max-width:560px; padding: 0 14px;`;

/* 전체 씬 이미지 히어로 */
const HeroSceneWrap = styled.div`
  width: 100%; max-width: 560px; margin: 0 auto;
  text-align: center; padding: 26px 20px 8px; background: #FBF6EA;
`;
const HeroSceneImg = styled.img`
  width: 150px; height: 150px; border-radius: 50%; object-fit: cover; object-position: center 30%;
  border: 2px solid #2B2A26; margin: 0 auto 10px; display: block;
  animation: ${floatHero} 3.5s ease-in-out infinite;
`;
const HeroSceneOverlay = styled.div`display:none;`;
const HeroTitleBlock = styled.div`
  text-align:center;
  text-align: center; z-index: 2;
`;
const HeroTitle = styled.div`
  font-size: 1.7rem; font-weight: 900; color: #2B2A26;
  text-shadow: 0 2px 8px rgba(255,251,240,0.8);
  margin-bottom: 4px;
`;
const HeroSub = styled.div`
  font-size: 0.8rem; color: #7A5C42; font-weight: 600;
  text-shadow: 0 1px 4px rgba(255,251,240,0.9);
`;

/* 섹션 헤더 */
const SectionLabel = styled.div`
  font-size: 0.95rem; font-weight: 900; color: #2B2A26;
  padding: 20px 0 12px;
  display: flex; align-items: center; gap: 8px;
  &::after { content:''; flex:1; height:1.5px; background:#EAE3D2; }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* 서비스 카드 — 이미지가 상단에 크게 */
const ServiceCardGrid = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 14px;
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;
const ServiceCard = styled.button<{ $color: string; $idx: number }>`
  background: #fff;
  border: 2px solid #2B2A26;
  border-radius: 20px;
  padding: 0;
  cursor: pointer; text-align: left;
  display: flex; flex-direction: column;
  transition: transform 0.2s, box-shadow 0.2s;
  animation: ${fadeUp} 0.4s ease both;
  animation-delay: ${p => p.$idx * 0.07}s;
  overflow: hidden; font-family: inherit;
  &:hover { transform: translateY(-4px); }
  &:active { transform: translateY(-2px); }
`;
/* 상단 큰 이미지 배너 + 훅 문구 오버레이 */
const SvcImgWrap = styled.div`position: relative; flex-shrink: 0;`;
const SvcImgBanner = styled.img`
  width: 100%; height: 168px; object-fit: cover; object-position: center 28%;
  border-bottom: 2px solid #2B2A26; display: block;
`;
const SvcImgCaption = styled.div`
  position: absolute; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.35);
  color: #fff; font-size: 12px; font-weight: 800; line-height: 1.4;
  padding: 8px 12px 10px; letter-spacing: -0.01em;
  text-shadow: 0 1px 4px rgba(0,0,0,0.9);
`;
const SvcCardBody = styled.div<{ $color: string }>`
  padding: 13px 14px 14px; flex: 1; display: flex; flex-direction: column;
  background: ${p => p.$color}22;
`;
const SvcHeadTitle = styled.div`font-size: 1.1rem; font-weight: 900; color: #2B2A26; letter-spacing: -0.02em; margin-bottom: 6px;`;
const SvcHook = styled.div<{ $color: string }>`
  font-size: 12.5px; font-weight: 700; color: #4A4640;
  line-height: 1.5; margin-bottom: 5px;
`;
const SvcSub = styled.div`font-size: 11px; color: #6B6355; font-weight: 600; line-height: 1.45; margin-bottom: 12px;`;
const SvcBtn = styled.div<{ $color: string }>`
  display: inline-flex; align-self: flex-start; margin-top: auto; align-items: center;
  font-size: 11.5px; font-weight: 800; color: #fff;
  background: ${p => p.$color}; border: 1.5px solid #2B2A26;
  border-radius: 999px; padding: 5px 13px;
`;

/* 홈 버튼 (섹션 라인 왼쪽) */
const HomeBtn = styled.button`
  display: inline-flex; align-items: center; gap: 5px; flex-shrink: 0;
  background: rgba(255,255,255,0.7); border: 1.5px solid #2B2A26; border-radius: 20px;
  padding: 5px 12px; cursor: pointer; font-size: 12px; font-weight: 700; color: #2B2A26; font-family: inherit;
`;

/* ─── STEP 1: 입력 폼 페이지 ────────────────────────────────────────── */
const FormWrap = styled.div`
  width: 100%;
  max-width: 520px;
`;
const BackBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: #8C8677;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 0 16px;
  font-family: inherit;
  &:hover { color: #8C8677; }
`;

const ServiceHeaderBand = styled.div<{ $color: string }>`
  background: ${p => p.$color}18;
  border-bottom: 2px solid ${p => p.$color}30;
  padding: 14px 18px 12px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;
const ServiceHeaderTextBlock = styled.div`flex: 1;`;
const ServiceHeaderImg = styled.img`
  width: 56px;
  height: 56px;
  object-fit: contain;
  border-radius: 50%;
  background: #FFF3DC;
  border: 2px solid #2B2A2644;
  flex-shrink: 0;
`;
const ServiceHeaderIcon = styled.span`font-size: 1.3rem; margin-right: 8px;`;
const ServiceHeaderHook = styled.div<{ $color: string }>`
  font-size: 14px;
  font-weight: 700;
  color: ${p => p.$color};
  line-height: 1.45;
`;
const ServiceHeaderSub = styled.div`font-size: 11px; color: #8C8677; margin-top: 3px;`;

const Card = styled.div`
  background: #fff;
  border-radius: 20px;
  padding: 0 0 28px;
  border: 2px solid #2B2A26;
  overflow: hidden;
`;

/* ─── 하위 옵션 칩 ──────────────────────────────────────────────────── */
const SubOptWrap = styled.div<{ $visible: boolean }>`
  overflow: hidden;
  max-height: ${p => p.$visible ? "150px" : "0"};
  transition: max-height 0.25s ease;
  background: #F8F9FA;
  border-bottom: 1px solid #2B2A26;
`;
const SubOptInner = styled.div`padding: 12px 16px;`;
const SubOptLabel = styled.div`font-size: 11px; font-weight: 700; color: #868E96; margin-bottom: 8px; letter-spacing: 0.06em;`;
const ChipRow = styled.div`display: flex; gap: 6px; flex-wrap: wrap;`;
const Chip = styled.button<{ $active: boolean }>`
  padding: 6px 14px;
  border-radius: 16px;
  border: 1.5px solid ${p => p.$active ? "#B8862A" : "#DEE2E6"};
  background: ${p => p.$active ? "#FFF3E6" : "#fff"};
  color: ${p => p.$active ? "#8C8677" : "#495057"};
  font-size: 12.5px;
  font-weight: ${p => p.$active ? 700 : 400};
  cursor: pointer;
  transition: all 0.15s;
  font-family: inherit;
  &:hover { border-color: #B8862A; }
`;

/* ─── 폼 ──────────────────────────────────────────────────────────── */
const FormBody = styled.form`padding: 20px 20px 0;`;
const SectionTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #8C8677;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 18px 0 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  &:first-child { margin-top: 0; }
`;
const FieldRow = styled.div`display: flex; gap: 12px; margin-bottom: 12px;`;
const Field = styled.div`flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px;`;
const Label = styled.label`font-size: 12px; color: #8C8677; font-weight: 600;`;
const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  border: 1.5px solid #2B2A26;
  border-radius: 10px;
  font-size: 0.9rem;
  color: #2B2A26;
  background: #FBF6EA;
  font-family: inherit;
  transition: border-color 0.2s;
  &:focus { outline: none; border-color: #B8862A; }
`;
const Select = styled.select`
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  border: 1.5px solid #2B2A26;
  border-radius: 10px;
  font-size: 0.9rem;
  color: #2B2A26;
  background: #FBF6EA;
  font-family: inherit;
  &:focus { outline: none; border-color: #B8862A; }
`;

/* ─── 파트너 폼 ─────────────────────────────────────────────────────── */
const CompatNoticeBand = styled.div`
  margin: 0;
  padding: 12px 20px;
  background: #FBE0D9;
  border-bottom: 1px solid #2B2A26;
`;
const CompatNoticeTitle = styled.div`
  font-size: 12px;
  font-weight: 800;
  color: #E03131;
  margin-bottom: 4px;
`;
const CompatNoticeText = styled.div`
  font-size: 11.5px;
  color: #555;
  line-height: 1.6;
`;
const PartnerWrap = styled.div<{ $visible: boolean }>`
  overflow: hidden;
  max-height: ${p => p.$visible ? "700px" : "0"};
  transition: max-height 0.3s ease;
`;
const PartnerDivider = styled.div`
  margin: 0 -20px 0;
  padding: 14px 20px 0;
  background: #fff;
  border-top: 2px dashed #EAE3D2;
`;
const PartnerTitle = styled.div`
  font-size: 13px;
  font-weight: 800;
  color: #1A1A2E;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
`;
const PartnerSub = styled.div`
  font-size: 11px;
  color: #868E96;
  margin-bottom: 14px;
`;

/* ─── 동의 / 제출 ───────────────────────────────────────────────────── */
const ConsentBox = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 0.8rem;
  color: #8C8677;
  line-height: 1.5;
  margin: 20px 0 0;
  cursor: pointer;
  input { margin-top: 2px; accent-color: #B8862A; width: 16px; height: 16px; flex-shrink: 0; }
`;
/* 리포트 보관 안내 — 재발급이 안 되므로 저장을 강하게 안내 */
const SaveNotice = styled.div`
  margin: 0 20px 14px;
  padding: 13px 15px;
  background: #FFF3BF;
  border: 2px solid #E8590C;
  border-radius: 12px;
  color: #7C2D12;
`;
const SaveNoticeTitle = styled.div`
  font-size: .9rem; font-weight: 900; margin-bottom: 4px;
`;
const SaveNoticeBody = styled.div`
  font-size: .78rem; line-height: 1.6;
`;
const SubmitBtn = styled.button<{ $disabled?: boolean }>`
  width: calc(100% - 40px);
  margin: 18px 20px 0;
  padding: 15px;
  background: ${p => p.$disabled ? "#ccc" : "#B8862A"};
  color: white;
  border: 2px solid #2B2A26;
  border-radius: 14px;
  font-size: 1rem;
  font-weight: 800;
  cursor: ${p => p.$disabled ? "not-allowed" : "pointer"};
  font-family: inherit;
  transition: opacity 0.2s;
  &:hover { opacity: ${p => p.$disabled ? 1 : 0.9}; }
`;
const ErrorMsg = styled.p`
  color: #c0392b;
  font-size: 0.82rem;
  margin: 10px 20px 0;
  text-align: center;
`;


/* ─── 로딩 오버레이 ──────────────────────────────────────────────── */
const floatAnim   = keyframes`0%,100%{transform:translateY(0)} 50%{transform:translateY(-12px)}`;
const dotBounce   = keyframes`0%,80%,100%{opacity:0.2;transform:scale(0.8)} 40%{opacity:1;transform:scale(1.2)}`;
const glowPulse   = keyframes`0%,100%{opacity:0.55}50%{opacity:1}`;
const lockPulse   = keyframes`0%,100%{transform:scale(1)} 50%{transform:scale(1.12)}`;
const revealText  = keyframes`from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)}`;
const sparkle     = keyframes`0%{opacity:0;transform:scale(0) rotate(0deg)} 50%{opacity:1;transform:scale(1) rotate(180deg)} 100%{opacity:0;transform:scale(0) rotate(360deg)}`;
const shimmerBg   = keyframes`0%{background-position:200% center} 100%{background-position:-200% center}`;

const LoadingOverlay = styled.div`
  position: fixed; inset: 0; z-index: 999;
  background: rgba(255,248,240,0.97);
  display: flex; flex-direction: column;
  align-items: center; justify-content: center; gap: 0;
`;
const LoadingChar = styled.img`
  width: 110px; height: 110px; object-fit: contain;
  animation: ${floatAnim} 2s ease-in-out infinite;
  filter: drop-shadow(0 8px 24px rgba(200,144,30,0.3));
  margin-bottom: 28px;
`;
const LoadingTitle = styled.div`
  font-size: 1.1rem; font-weight: 900; color: #2B2A26;
  margin-bottom: 10px; letter-spacing: -0.3px;
`;
const LoadingDots = styled.div`
  display: flex; gap: 7px; margin-bottom: 28px;
  span {
    width: 8px; height: 8px; border-radius: 50%;
    background: #B8862A; display: inline-block;
    animation: ${dotBounce} 1.2s ease-in-out infinite;
    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }
`;
const LoadingProgress = styled.div`
  width: 200px; height: 3px; background: #FBF6EA; border-radius: 999px; overflow: hidden;
  &::after {
    content: ''; display: block; height: 100%;
    background: #B8862A;
    background-size: 200% auto;
    border-radius: 999px;
    animation: ${shimmerBg} 1.5s linear infinite;
  }
`;

/* ─── 미리보기 화면 ──────────────────────────────────────────────── */
const PreviewPage = styled.div`
  width: 100%; min-height: 100vh;
  background: #FBF6EA;
  display: flex; flex-direction: column; align-items: center;
  padding: 0 0 60px;
`;
const PreviewTopBar = styled.div`
  height: 6px; width: 100%;
  background: #FBF6EA;
`;
const PreviewHero = styled.div`
  width: 100%; max-width: 520px;
  padding: 24px 20px 0;
  display: flex; align-items: center; gap: 14px;
  animation: ${revealText} 0.5s ease both;
`;
const PreviewCharImg = styled.img`
  width: 60px; height: 60px;
  object-fit: contain;
  filter: drop-shadow(0 4px 10px rgba(200,144,30,0.3));
  flex-shrink: 0;
`;
const PreviewBrandBox = styled.div`flex: 1;`;
const PreviewBrand = styled.div`
  font-size: 1.1rem; font-weight: 900; color: #2B2A26;
`;
const PreviewBrandSub = styled.div`font-size: 0.72rem; color: #8C8677; margin-top: 2px;`;

const PreviewBadge = styled.div<{ $color: string }>`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 5px 14px;
  background: ${p => p.$color}18;
  border: 1.5px solid ${p => p.$color}55;
  border-radius: 20px;
  font-size: 0.75rem; font-weight: 800;
  color: ${p => p.$color};
  margin: 20px 20px 0;
  animation: ${revealText} 0.5s 0.1s ease both; opacity: 0;
  animation-fill-mode: forwards;
`;

const PreviewCard = styled.div`
  width: calc(100% - 32px); max-width: 488px;
  margin: 14px 16px 0;
  background: #fff;
  border-radius: 22px;
  border: 2px solid #2B2A26;
  overflow: hidden;
  animation: ${revealText} 0.5s 0.2s ease both; opacity: 0;
  animation-fill-mode: forwards;
`;
const PreviewCardTop = styled.div`
  height: 5px;
  background: #FBF6EA;
`;
const PreviewCardInner = styled.div`padding: 22px 20px 0;`;
const PreviewAnalysisTitle = styled.div`
  font-size: 1rem; font-weight: 900; color: #2B2A26;
  margin-bottom: 14px; display: flex; align-items: center; gap: 8px;
`;
const PreviewTextBoxOuter = styled.div`
  position: relative;
  height: 420px;
  overflow: hidden;
`;
const PreviewScrollArea = styled.div`
  height: 100%;
  overflow-y: auto;
  padding: 0 4px 80px;
  scrollbar-width: thin;
  scrollbar-color: #EAE3D2 transparent;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: #EAE3D2; border-radius: 4px; }
`;
const PreviewTextBox = styled.div`
  font-size: 0.92rem; color: #2B2A26; line-height: 2.2;
  text-align: left;
  white-space: pre-line;
  word-break: keep-all;
  animation: ${revealText} 0.7s 0.4s ease both; opacity: 0;
  animation-fill-mode: forwards;
`;
const PreviewFade = styled.div`
  position: absolute; bottom: 0; left: 0; right: 0; height: 140px;
  background: linear-gradient(to bottom, transparent 0%, rgba(255,255,255,0.8) 50%, #fff 100%);
  pointer-events: none;
`;
const PreviewLockSection = styled.div`
  padding: 8px 20px 20px;
  text-align: center;
  border-top: 1px solid #2B2A26;
  margin-top: 8px;
`;
const LockIcon = styled.div`
  font-size: 2rem; margin-bottom: 6px;
  animation: ${lockPulse} 2s ease-in-out infinite;
  display: inline-block;
`;
const LockTitle = styled.div`
  font-size: 0.95rem; font-weight: 900; color: #2B2A26;
  margin-bottom: 4px;
`;
const LockSub = styled.div`font-size: 0.75rem; color: #8C8677; margin-bottom: 14px; line-height: 1.6;`;

const UnlockList = styled.div`
  background: #FFF8EE; border-radius: 12px;
  padding: 12px 14px; margin-bottom: 18px; text-align: left;
`;
const UnlockItem = styled.div<{ $delay: number }>`
  font-size: 0.78rem; color: #5A3E2E;
  padding: 4px 0; display: flex; align-items: flex-start; gap: 7px;
  animation: ${revealText} 0.4s ${p => p.$delay}s ease both; opacity: 0;
  animation-fill-mode: forwards;
  &::before { content: '✓'; color: #B8862A; font-weight: 900; flex-shrink: 0; }
`;
const PayBtn = styled.button`
  width: 100%; padding: 16px;
  background: #B8862A;
  background-size: 200% auto;
  color: #fff; border: 2px solid #2B2A26; border-radius: 14px;
  font-size: 1.05rem; font-weight: 900;
  cursor: pointer; font-family: inherit;
  animation: ${glowPulse} 2s ease-in-out infinite;
  transition: transform 0.15s;
  letter-spacing: -0.3px;
  &:active { transform: scale(0.97); }
`;
const PayInfo = styled.div`
  font-size: 0.72rem; color: #8C8677; text-align: center;
  margin-top: 10px; margin-bottom: 4px; line-height: 1.8;
`;
const SparkleWrap = styled.div`
  position: absolute; inset: 0; pointer-events: none; overflow: hidden;
`;
const SparkleItem = styled.div<{ $x: number; $y: number; $delay: number; $size: number }>`
  position: absolute;
  left: ${p => p.$x}%; top: ${p => p.$y}%;
  font-size: ${p => p.$size}rem;
  animation: ${sparkle} 3s ${p => p.$delay}s ease-in-out infinite;
`;

/* ─── 완료 카드 (레거시, 무료발송 모드용) ────────────────────────── */
const SuccessCard = styled.div`
  width: 100%; max-width: 520px;
  background: #fff; border: 1.5px solid #2B2A26;
  border-radius: 20px; padding: 48px 24px; text-align: center;
`;
const SuccessIcon = styled.div`font-size: 3rem; margin-bottom: 16px;`;
const SuccessTitle = styled.h2`font-size: 1.3rem; color: #2B2A26; margin-bottom: 10px;`;
const SuccessDesc = styled.p`font-size: 0.88rem; color: #8C8677; line-height: 1.7;`;
const ServiceBadge = styled.span`
  display: inline-block; padding: 4px 12px;
  background: #FFF3E6; border: 1px solid #2B2A26;
  border-radius: 20px; font-size: 12px; font-weight: 700; color: #8C8677; margin-bottom: 16px;
`;
const ResetBtn = styled.button`
  margin-top: 16px; padding: 11px 28px;
  background: transparent; color: #8C8677;
  border: 1.5px solid #2B2A26; border-radius: 50px;
  font-size: 0.85rem; font-weight: 700; cursor: pointer; font-family: inherit;
  &:hover { background: #f9f4ef; }
`;

/* ─── 상수 ───────────────────────────────────────────────────────────── */
const API = process.env.REACT_APP_API_URL || "";
const BIRTH_TYPES = [
  { value: "solar", label: "양력" },
  { value: "lunar", label: "음력" },
  { value: "lunar_leap", label: "음력 윤달" },
];
const CITY_GROUPS = [
  { label: "🇰🇷 국내", cities: ["서울","부산","대구","인천","광주","대전","울산","제주","수원","성남","용인","청주","전주","강릉","포항","창원","고양","춘천","목포","세종"] },
  { label: "🌏 동아시아", cities: ["도쿄","오사카","베이징","상하이","홍콩","타이베이","마카오","울란바토르"] },
  { label: "🌏 동남아시아", cities: ["방콕","싱가포르","쿠알라룸푸르","자카르타","마닐라","하노이","호치민","양곤","프놈펜","비엔티안"] },
  { label: "🌏 남아시아·중앙아시아", cities: ["뉴델리","뭄바이","다카","카라치","이슬라마바드","콜롬보","카트만두","타슈켄트","알마티","아스타나"] },
  { label: "🌍 중동", cities: ["두바이","아부다비","도하","리야드","테헤란","이스탄불","앙카라","바그다드","베이루트","암만","예루살렘","쿠웨이트시티"] },
  { label: "🌍 서유럽", cities: ["런던","파리","베를린","마드리드","로마","암스테르담","브뤼셀","취리히","비엔나","코펜하겐","스톡홀름","오슬로","헬싱키","리스본","더블린"] },
  { label: "🌍 동유럽·러시아", cities: ["모스크바","상트페테르부르크","키이우","바르샤바","프라하","부다페스트","부쿠레슈티","아테네","소피아","베오그라드","민스크","탈린","리가","빌뉴스"] },
  { label: "🌎 북미", cities: ["뉴욕","워싱턴DC","로스앤젤레스","시카고","샌프란시스코","시애틀","토론토","밴쿠버","오타와","멕시코시티"] },
  { label: "🌎 중남미·카리브", cities: ["보고타","상파울루","부에노스아이레스","리마","산티아고","카라카스","브라질리아","몬테비데오","아바나","파나마시티"] },
  { label: "🌏 오세아니아", cities: ["시드니","멜버른","캔버라","브리즈번","오클랜드","웰링턴"] },
  { label: "🌍 아프리카", cities: ["카이로","나이로비","요하네스버그","라고스","아디스아바바","카사블랑카","다카르","아크라","알제","튀니스"] },
];

/* 이미지별 최적 배경 위치 (캐릭터가 잘 보이도록) */
const IMG_BG_POS: Record<string, string> = {
  "/woo_02.webp": "center 30%",  // 연애궁합/심층궁합: 캐릭터 중앙
  "/woo_03.webp": "center 65%",  // 신년운세: 달 위 캐릭터 하단 위치
  "/woo_04.webp": "center 25%",  // 종합운세: 두루마리 캐릭터 상단
  "/woo_05.webp": "center 30%",  // 직업/사업운: 캐릭터 중앙상단
  "/woo_06.webp": "center 30%",  // 쿠키사주: 요리 캐릭터
  "/woo_07.webp": "center 55%",  // 건강운: 해먹 캐릭터 하단
  "/woo_08.webp": "center 30%",  // 연애운: 티타임 캐릭터
};

const SERVICE_TYPES = [
  { key: "종합운세",    color: "#5E8A4E", icon: "", img: "/woo_04.webp", iconImg: "/woo_y_3.webp", hook: "내 운명은 어떨까? '운의 파도'를 확인하세요.", sub: "타고난 명운과 인생 전체 흐름 정밀 분석" },
  { key: "신년운세",    color: "#4A7BA8", icon: "", img: "/woo_03.webp", iconImg: "/woo_y_5.webp", hook: "새해, 운의 흐름을 먼저 읽는 자가 앞서 갑니다.", sub: "올해 월별 운세 전체 흐름 분석" },
  { key: "연애운",      color: "#D0654F", icon: "", img: "/woo_08.webp", iconImg: "/woo_y_1.webp", hook: "인연은 준비된 사람에게 찾아옵니다.", sub: "인연을 만나는 시기와 상대방 특징 분석" },
  { key: "연애궁합",    color: "#B85C7A", icon: "", img: "/woo_02.webp", iconImg: "/woo_y_2.webp", hook: "끌리는 이유가 있습니다. 우리는 인연일까?", sub: "궁합, 첫만남은 언제일지 사주로 알려드립니다." },
  { key: "심층궁합",    color: "#B85C7A", icon: "", img: "/woo_01.webp", iconImg: "/woo_y_2.webp", hook: "결혼까지 가는 사람인지, 사주가 알고 있습니다.", sub: "결혼운·자녀운·이별위험시기 정밀 분석" },
  { key: "직업운",      color: "#B8862A", icon: "", img: "/woo_05.webp", iconImg: "/woo_y_6.webp", hook: "어떤 직업을 선택해야할까?", sub: "커리어 전환·승진의 최적 시기 분석" },
  { key: "사업(재물운)", color: "#B8862A", icon: "", img: "/woo_06.webp", iconImg: "/woo_y_3.webp", hook: "나의 타고난 재물운", sub: "내 재물이 터지는 구체적인 시기 확인" },
  { key: "건강운",      color: "#5E8A4E", icon: "", img: "/woo_07.webp", iconImg: "/woo_y_5.webp", hook: "몸이 보내는 신호, 사주가 먼저 알고 있습니다.", sub: "취약 부위·사고수·수술수 시기 분석" },
];

/* 서비스별 결제 금액 (2026-08-04 개편)
   서버(client.py)의 가격표가 기준이며, 접수 응답으로 실제 금액이 내려온다.
   여기 값은 응답이 오기 전에 화면에 잠깐 쓰는 예비값이다. 서버와 반드시 같게 유지할 것. */
const SVC_PRICE: Record<string, number> = {
  "종합운세": 24900, "신년운세": 9900,
  "연애운": 7900,    "연애궁합": 14900,
  "심층궁합": 29000, "직업운": 7900,
  "사업(재물운)": 7900, "건강운": 7900,
};

/* 결제 후 잠금 해제 콘텐츠 설명 */
const SVC_UNLOCK: Record<string, string[]> = {
  "종합운세":    ["타고난 격국·용신 분석", "결혼·직업·건강 구체적 년도 예측", "대운별 인생 흐름 서술"],
  "신년운세":    ["1~12월 월별 운세", "월별 용신·기신 날짜", "주의 시기 상세 설명"],
  "연애운":      ["인연 만날 가능성 높은 날짜", "상대방 외모·성격·직업 특징", "이별수·주의 시기"],
  "연애궁합":    ["첫 만남 년도 예측", "한난조습 에너지 분석", "속궁합·성격궁합·가치관 상세"],
  "심층궁합":    ["성격 궁합·속궁합", "결혼운·자녀운", "이별 위험 시기 전체"],
  "직업운":      ["합격·승진 최적일 TOP 3", "이직 황금 타이밍", "직종 추천"],
  "사업(재물운)": ["계약·시작 최적일 TOP 3", "재물 터지는 시기", "업종 추천"],
  "건강운":      ["취약 부위 상세 분석", "사고수·수술수 위험 시기", "건강 관리 최적 시기"],
};

const COMPAT_SERVICES = ["심층궁합", "연애궁합"];

const ADULT_OPTIONS = ["연애운", "직업운", "사업(재물운)", "건강운"];
const MINOR_OPTIONS = ["학업운", "가족/교우관계", "연애운", "건강운"];

function parseTime(val: string) {
  const s = val.replace(/\s/g, "");
  if (!s) return { hour: 99, minute: 0 };
  let h: number, m: number;
  if (s.includes(":")) {
    const parts = s.split(":");
    h = parseInt(parts[0]); m = parseInt(parts[1] || "0");
  } else if (/^\d{3,4}$/.test(s)) {
    const p = s.padStart(4, "0");
    h = parseInt(p.slice(0, 2)); m = parseInt(p.slice(2));
  } else {
    h = parseInt(s); m = 0;
  }
  return {
    hour: (!isNaN(h) && h >= 0 && h <= 23) ? h : 99,
    minute: (!isNaN(m) && m >= 0 && m <= 59) ? m : 0,
  };
}

function parseBirthDate(val: string) {
  const sep = val.includes("/") ? "/" : val.includes(".") ? "." : val.includes("-") ? "-" : null;
  if (sep) { const [y,m,d] = val.split(sep).map(Number); return { year:y||0,month:m||0,day:d||0 }; }
  if (val.length === 8) return { year:Number(val.slice(0,4)),month:Number(val.slice(4,6)),day:Number(val.slice(6,8)) };
  return { year:0,month:0,day:0 };
}

interface PartnerForm {
  name: string; gender: string; birth_type: string;
  birth_date: string;
  birth_time: string;
  birth_time_unknown: boolean;
  city: string;
}
const defaultPartner: PartnerForm = {
  name: "", gender: "female", birth_type: "solar",
  birth_date: "",
  birth_time: "",
  birth_time_unknown: false,
  city: "서울",
};

/* ─── 시주 추정 설문 모달 styled ────────────────────────────────────── */
const SurveyOverlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.55); z-index: 999;
  display: flex; align-items: center; justify-content: center; padding: 16px;
`;
const SurveyModal = styled.div`
  background: #fff; border-radius: 18px; width: 100%; max-width: 520px;
  max-height: 88vh; overflow-y: auto; padding: 26px 22px;
`;
const SurveyQBlock = styled.div` margin-bottom: 18px; `;
const SurveyQText = styled.div`
  font-size: 0.83rem; font-weight: 700; color: #2B2A26; margin-bottom: 7px;
`;
const SurveyChoiceLabel = styled.label<{ $selected: boolean }>`
  display: flex; align-items: flex-start; gap: 8px;
  padding: 8px 12px; border-radius: 8px; cursor: pointer; font-size: 0.8rem;
  margin-bottom: 5px;
  background: ${p => p.$selected ? "#FFF3E0" : "#F8F4F0"};
  border: 1.5px solid ${p => p.$selected ? "#B8862A" : "#e8dfd5"};
  color: ${p => p.$selected ? "#7a4f2d" : "#5a4535"};
  transition: all 0.15s;
  &:hover { background: #FFF0E3; border-color: #B8862A; }
`;
const SurveyResultItem = styled.div<{ $rank: number }>`
  display: flex; align-items: center; gap: 10px;
  padding: 11px 14px; border-radius: 10px; margin-bottom: 7px;
  background: ${p => p.$rank === 1 ? "#FFF9DB" : p.$rank === 2 ? "#F3F0FF" : "#F0F8FF"};
  border: 1.5px solid ${p => p.$rank === 1 ? "#B8862A" : p.$rank === 2 ? "#9775FA" : "#74C0FC"};
`;

/* 서비스 표시 텍스트(훅/설명) i18n 키 슬러그 매핑 */
const SVC_SLUG: Record<string, string> = {
  "종합운세": "Comprehensive",
  "신년운세": "NewYear",
  "연애운": "Love",
  "연애궁합": "LoveCompat",
  "심층궁합": "DeepCompat",
  "직업운": "Career",
  "사업(재물운)": "Business",
  "건강운": "Health",
};

export default function ClientPage() {
  const { lang, t } = useLang();
  const [step,        setStep]        = useState<0 | 1>(0);
  const [serviceType, setServiceType] = useState("종합운세");

  /* 앞서 결제한 접수번호 — 윗 상품으로 올라갈 때 낸 금액을 빼주기 위해 들고 다닌다.
     실제 차감 여부와 금액은 서버가 다시 계산한다(화면 값은 믿지 않는다). */
  const [upgradeFrom, setUpgradeFrom] = useState<number | null>(null);
  /* 이어받기가 끝나면 자동으로 접수까지 진행한다 — 사용자가 다시 누를 게 없다 */
  const [autoSubmit, setAutoSubmit] = useState(false);
  /* 이어받는 동안 입력폼이 잠깐 스쳐 보이면 '또 입력하라는 건가' 싶어진다 */
  const [resuming,   setResuming]   = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const svc = params.get("service");
    if (svc && SERVICE_TYPES.some(s => s.key === svc)) {
      setServiceType(svc);
      setStep(1);
    }
    const from = Number(params.get("from") || "");
    if (from > 0) {
      setUpgradeFrom(from);
      setResuming(true);
      // 앞서 접수한 정보를 그대로 이어받는다 — 생년월일을 다시 받으면 여기서 다 빠져나간다.
      (async () => {
        try {
          const r = await axios.get(`${API}/api/v1/client/prefill/${from}`);
          const d = r.data || {};
          const pad = (n: any) => String(n ?? "").padStart(2, "0");
          const dateOf = (o: any) => (o?.birth_year
            ? `${o.birth_year}/${pad(o.birth_month)}/${pad(o.birth_day)}` : "");
          const timeOf = (o: any) => ((o?.birth_hour === 99 || o?.birth_hour == null)
            ? "" : `${pad(o.birth_hour)}:${pad(o.birth_minute || 0)}`);
          setForm(f => ({
            ...f,
            name: d.name || f.name,
            email: d.email || f.email,
            gender: d.gender || f.gender,
            birth_date: dateOf(d) || f.birth_date,
            birth_time: timeOf(d),
            birth_time_unknown: d.birth_hour === 99 || d.birth_hour == null,
            birth_type: d.birth_type || "solar",
            city: d.city || "서울",
            consent: true,          // 앞서 결제할 때 이미 동의했다
          }));
          if (d.partner_info) {
            const p = d.partner_info;
            setPartner(pt => ({
              ...pt,
              name: p.name || pt.name,
              gender: p.gender || pt.gender,
              birth_type: p.birth_type || "solar",
              birth_date: dateOf(p) || pt.birth_date,
              birth_time: timeOf(p),
              birth_time_unknown: p.birth_hour === 99 || p.birth_hour == null,
              city: p.city || "서울",
            }));
          }
          setAutoSubmit(true);      // 입력폼을 거치지 않고 바로 결제 화면으로
        } catch {
          // 이어받기 실패하면 그냥 입력폼을 보여준다(차감은 서버가 따로 판단)
          setResuming(false);
          setStep(1);
        }
      })();
    }
  }, []);

  const [options,     setOptions]     = useState<string[]>([]);
  const [form,        setForm]        = useState({
    name: "", email: "", gender: "female",
    birth_date: "",
    birth_time: "",
    birth_time_unknown: false,
    birth_type: "solar", city: "서울", consent: false,
  });
  const [partner,  setPartner]  = useState<PartnerForm>(defaultPartner);

  /* 쿠키 사주(무료)에서 '더 보기'로 넘어온 경우 — 입력값을 그대로 이어받는다.
     생년월일을 다시 쓰게 만들면 여기서 대부분 이탈한다. */
  useEffect(() => {
    let saved: any = null;
    try {
      const raw = sessionStorage.getItem("wooyeonPrefill");
      if (raw) { saved = JSON.parse(raw); sessionStorage.removeItem("wooyeonPrefill"); }
    } catch { /* 이어받기 실패해도 직접 입력하면 된다 */ }
    if (!saved) return;

    if (saved.serviceType && SERVICE_TYPES.some(s => s.key === saved.serviceType)) {
      setServiceType(saved.serviceType);
    }
    const p1 = saved.person1 || {};
    setForm(f => ({
      ...f,
      name:  p1.name  || f.name,
      email: saved.email || f.email,
      gender: p1.gender || f.gender,
      birth_date: p1.birth_date || f.birth_date,
      birth_time: p1.birth_time || f.birth_time,
      birth_time_unknown: !!p1.birth_time_unknown,
      birth_type: p1.birth_type || f.birth_type,
      city: p1.city || f.city,
    }));
    if (saved.needPartner && saved.person2) {
      const p2 = saved.person2;
      setPartner(pt => ({
        ...pt,
        name: p2.name || pt.name,
        gender: p2.gender || pt.gender,
        birth_type: p2.birth_type || pt.birth_type,
        birth_date: p2.birth_date || pt.birth_date,
        birth_time: p2.birth_time || pt.birth_time,
        birth_time_unknown: !!p2.birth_time_unknown,
        city: p2.city || pt.city,
      }));
    }
    setStep(1);   // 서비스는 정해졌으니 입력 단계로
  }, []);

  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");
  /* ─── 시주 추정 설문 state ─── */
  const [surveying,     setSurveying]     = useState<"self"|"partner"|null>(null);
  const [surveyQs,      setSurveyQs]      = useState<{id:number;question:string;choices:string[]}[]>([]);
  const [surveyLang,    setSurveyLang]    = useState<string>("");
  const [surveyAns,     setSurveyAns]     = useState<number[]>(Array(10).fill(-1));
  const [surveyResults, setSurveyResults] = useState<any[]>([]);
  // 제출 후 미리보기 상태
  const [preview, setPreview] = useState<{
    consultationId: number;
    text: string;
    serviceType: string;
    paymentEnabled: boolean;
    emailSent: boolean;
    freeHtml?: string;        // 결제 전에 보여주는 무료 구간 결과지
    lockedSections?: string[];  // 결제하면 열리는 꼭지 제목들
    lockedItems?: { title: string; desc: string }[];
    hookHead?: string;
    hookBody?: string;
    hookVolume?: string;   // "15개 분야 · 77개 항목 · 15,214자" — 가격 근거
    price?: number;             // 실제 결제할 금액(차감 반영)
    originalPrice?: number;     // 차감 전 정가
    credit?: number;            // 앞서 낸 금액에서 빼준 액수
  } | null>(null);

  const age = useMemo(() => {
    const { year } = parseBirthDate(form.birth_date);
    if (!year) return null;
    return new Date().getFullYear() - year;
  }, [form.birth_date]);
  const isMinor = age !== null && age < 20;
  const currentOptions = isMinor ? MINOR_OPTIONS : ADULT_OPTIONS;
  const isCompat = COMPAT_SERVICES.includes(serviceType);

  const openSurvey = async (target: "self" | "partner") => {
    // 설문 문항은 언어별로 다르므로 언어가 바뀌면 다시 받아온다
    if (surveyQs.length === 0 || surveyLang !== lang) {
      try {
        const r = await axios.get(`${process.env.REACT_APP_API_URL || ""}/api/v1/saju/hour-survey`, { params: { lang } });
        setSurveyQs(r.data.questions);
        setSurveyLang(lang);
      } catch { return; }
    }
    setSurveyAns(Array(10).fill(-1));
    setSurveyResults([]);
    setSurveying(target);
  };
  const closeSurvey = () => setSurveying(null);
  const submitSurvey = async () => {
    if (surveyAns.some(a => a < 0)) return;
    try {
      const r = await axios.post(`${process.env.REACT_APP_API_URL || ""}/api/v1/saju/estimate-hour`, { answers: surveyAns, lang });
      setSurveyResults(r.data.recommendations);
    } catch {}
  };
  const applySurveyResult = (birthHour: number) => {
    const timeStr = String(birthHour).padStart(2,"0") + ":00";
    if (surveying === "self") {
      setForm(f => ({ ...f, birth_time: timeStr, birth_time_unknown: false }));
    } else if (surveying === "partner") {
      setPartner(p => ({ ...p, birth_time: timeStr, birth_time_unknown: false }));
    }
    setSurveying(null);
  };

  const set  = (key: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm(f => ({ ...f, [key]: e.target.value }));
  const setP = (key: keyof PartnerForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setPartner(p => ({ ...p, [key]: e.target.value }));
  const toggleOption = (opt: string) =>
    setOptions(prev => prev.includes(opt) ? prev.filter(o => o !== opt) : [...prev, opt]);

  const selectService = (svc: string) => {
    setServiceType(svc);
    setOptions([]);
    setStep(1);           // 서비스 선택 → 입력 폼으로 이동
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!form.consent) { setError(t("client.errConsent")); return; }
    if (!form.email || !form.email.includes("@")) {
      setError(t("client.errEmail")); return;
    }
    if (!form.name || !form.birth_date) {
      setError(t("client.errRequired")); return;
    }
    const { year: by, month: bm, day: bd } = parseBirthDate(form.birth_date);
    if (!by || !bm || !bd) {
      setError(t("client.errBirthDate")); return;
    }
    if (isCompat) {
      const pd = parseBirthDate(partner.birth_date);
      if (!partner.name || !pd.year || !pd.month || !pd.day) {
        setError(t("client.errPartner")); return;
      }
    }
    setLoading(true);
    setError("");
    try {
      const pd = parseBirthDate(partner.birth_date);
      const res = await axios.post(`${API}/api/v1/client/submit`, {
        name: form.name,
        email: form.email || "",
        gender: form.gender,
        birth_year: by,
        birth_month: bm,
        birth_day: bd,
        birth_hour: form.birth_time_unknown ? 99 : parseTime(form.birth_time).hour,
        birth_minute: form.birth_time_unknown ? 0 : parseTime(form.birth_time).minute,
        birth_type: form.birth_type,
        city: form.city,
        consent: true,
        lang,
        service_type: serviceType,
        service_options: options,
        upgrade_from: upgradeFrom,
        partner_info: isCompat ? {
          name: partner.name,
          gender: partner.gender,
          birth_type: partner.birth_type,
          birth_year: pd.year,
          birth_month: pd.month,
          birth_day: pd.day,
          birth_hour: partner.birth_time_unknown ? 99 : parseTime(partner.birth_time).hour,
          birth_minute: partner.birth_time_unknown ? 0 : parseTime(partner.birth_time).minute,
          city: partner.city,
          compatibility_type: serviceType === "심층궁합" ? "deep" : "casual",
        } : null,
      });
      setPreview({
        consultationId: res.data.consultation_id,
        text: res.data.preview_text || t("client.analysisCompleteText"),
        serviceType: res.data.service_type || serviceType,
        paymentEnabled: res.data.payment_enabled ?? true,
        emailSent: res.data.email_sent ?? false,
        freeHtml: res.data.free_html || "",
        lockedSections: res.data.locked_sections || [],
        lockedItems: res.data.locked_items || [],
        hookHead: (res.data.upsell_hook || {}).headline || "",
        hookBody: (res.data.upsell_hook || {}).body || "",
        hookVolume: (res.data.upsell_hook || {}).volume || "",
        price: res.data.price || 0,
        originalPrice: res.data.original_price || 0,
        credit: res.data.credit || 0,
      });
    } catch (err: any) {
      setError(err.response?.data?.detail || t("client.errSubmit"));
    } finally {
      setLoading(false);
    }
  };

  /* 이어받기가 끝났으면 입력폼을 건너뛰고 바로 접수한다.
     여기서 폼을 한 번 더 보여주면 "또 입력하라는 거냐"가 된다. */
  useEffect(() => {
    if (!autoSubmit || loading || preview) return;
    if (!form.name || !form.birth_date) return;
    if (isCompat && !partner.birth_date) return;
    setAutoSubmit(false);
    setResuming(false);
    handleSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSubmit, form, partner, isCompat]);

  // 포트원 결제 핸들러
  const handlePayment = () => {
    if (!preview) return;
    const IMP = (window as any).IMP;
    if (!IMP) { alert(t("client.alertPayLoading")); return; }
    requestPay({
      merchantUid: `saju_${preview.consultationId}_${Date.now()}`,
      name: t("client.payName", { svc: t(`svc.${preview.serviceType}`) }),
      // 서버가 내려준 금액을 그대로 쓴다(서버 가격표가 기준). 없을 때만 예비값.
      amount: preview.price || SVC_PRICE[preview.serviceType] || 7900,
      buyerName: form.name,
      buyerEmail: form.email || "",
      redirectUrl: `${window.location.origin}/payment/success`,
    }).then(async (rsp) => {
      if (rsp.success) {
        /* 결제는 이미 끝났다 — 확인 요청이 한 번 미끄러졌다고 포기하면
           돈만 빠져나간 화면이 된다. 사이를 두고 세 번까지 다시 부른다. */
        for (let attempt = 1; attempt <= 3; attempt++) {
          try {
            await axios.post(`${API}/api/v1/client/payment-verify`, {
              consultation_id: preview.consultationId,
              imp_uid: rsp.imp_uid,
              merchant_uid: rsp.merchant_uid,
            }, { timeout: 120000 });
            window.location.href = `/report/${preview.consultationId}`;
            return;
          } catch (e: any) {
            if (attempt < 3) {
              await new Promise(r => setTimeout(r, attempt * 2000));
              continue;
            }
            // 그래도 안 되면 접수번호를 알려준다 — 이 번호만 있으면 다시 받을 수 있다
            alert(
              `${e?.response?.data?.detail || t("client.alertPayProcessError")}\n\n` +
              `접수번호 ${preview.consultationId}`,
            );
            window.location.href = `/report/${preview.consultationId}`;
            return;
          }
        }
      } else {
        alert(t("client.alertPayFail", { msg: rsp.error_msg || t("client.payRetry") }));
      }
    });
  };

  /* ─── 로딩 화면 ─── */
  if (loading || resuming) {
    return (
      <>
        <Global />
        <LoadingOverlay>
          <LoadingChar src="/woo_y_1.webp" alt={t("client.loadingTitle")} />
          <LoadingTitle>{t("client.loadingTitle")}</LoadingTitle>
          <LoadingDots><span /><span /><span /></LoadingDots>
          <LoadingProgress />
          <div style={{ fontSize: "0.75rem", color: "#8C8677", marginTop: 16 }}>
            {t("client.loadingSub")}
          </div>
        </LoadingOverlay>
      </>
    );
  }

  /* ─── 미리보기 + 결제 화면 ─── */
  if (preview) {
    const svc = SERVICE_TYPES.find(s => s.key === preview.serviceType);
    const color = svc?.color || "#B8862A";
    const unlockItems = SVC_UNLOCK[preview.serviceType] || [];
    // 금액·무료 구간·잠긴 꼭지는 모두 서버가 내려준 값을 우선 쓴다.
    const price = preview.price || SVC_PRICE[preview.serviceType] || 7900;
    const freeHtml = preview.freeHtml || "";
    const lockedList = (preview.lockedSections && preview.lockedSections.length)
      ? preview.lockedSections
      : unlockItems;
    // 서버가 설명까지 붙여 보내면 그걸 쓰고, 없으면 제목만이라도 보여준다
    const lockedCards = (preview.lockedItems && preview.lockedItems.length)
      ? preview.lockedItems
      : lockedList.map(t2 => ({ title: t2, desc: "" }));

    /* 무료발송 모드 (PAYMENT_ENABLED=false) */
    if (!preview.paymentEnabled) {
      return (
        <>
          <Global />
          <PreviewPage>
            <PreviewTopBar />
            <PreviewHero>
              <PreviewCharImg src="/woo_y_1.webp" alt="우연" />
              <PreviewBrandBox>
                <PreviewBrand>{t("client.brandName")}</PreviewBrand>
                <PreviewBrandSub>{t("client.previewCompleteSub")}</PreviewBrandSub>
              </PreviewBrandBox>
            </PreviewHero>
            <PreviewBadge $color={color}>{t(`svc.${preview.serviceType}`)}</PreviewBadge>
            <PreviewCard>
              <PreviewCardTop />
              <PreviewCardInner>
                <PreviewAnalysisTitle>{t("client.previewResultTitle", { name: form.name })}</PreviewAnalysisTitle>
                {(() => {
                  const pv = splitPreview(preview.text);
                  return (
                    <>
                      <div style={{ fontSize: "0.88rem", color: "#2B2A26", lineHeight: 1.9 }}>
                        {pv.head}
                      </div>
                      <div style={{ paddingBottom: 20 }}>
                        <PreviewHighlights intro={pv.intro} items={pv.items} color={color} />
                      </div>
                    </>
                  );
                })()}
              </PreviewCardInner>
              <PreviewLockSection>
                {form.email ? (
                  <div style={{ padding: "12px 0", background: "#ebfbee", borderRadius: 14, marginBottom: 14 }}>
                    <div style={{ fontSize: "1.4rem", marginBottom: 6 }}>✅</div>
                    <div style={{ fontSize: "0.9rem", fontWeight: 800, color: "#2f9e44" }}>
                      {preview.emailSent
                        ? <>{t("client.reportSentPre")}<strong>{form.email}</strong>{t("client.reportSentPost")}</>
                        : <>{t("client.reportSendingPre")}<strong>{form.email}</strong>{t("client.reportSendingPost")}</>}
                    </div>
                  </div>
                ) : null}
                <SaveNotice style={{ margin: "0 0 12px" }}>
                  <SaveNoticeTitle>⚠️ {t("common.saveWarnTitle")}</SaveNoticeTitle>
                  <SaveNoticeBody>{t("common.saveWarnAfter")}</SaveNoticeBody>
                </SaveNotice>
                <PayBtn
                  onClick={() => { window.location.href = `/report/${preview.consultationId}`; }}
                  style={{ background: color, border: "2px solid #2B2A26" }}
                >
                  {t("client.viewFullReport")}
                </PayBtn>
              </PreviewLockSection>
            </PreviewCard>
            <div style={{ padding: "16px 20px 0", width: "100%", maxWidth: 520 }}>
              <ResetBtn onClick={() => {
                setPreview(null); setStep(0);
                setForm({ name: "", email: "", gender: "female", birth_date: "", birth_time: "", birth_time_unknown: false, birth_type: "solar", city: "서울", consent: false });
                setPartner(defaultPartner); setOptions([]); setError("");
              }}>← {t("client.resetBtn")}</ResetBtn>
            </div>
          </PreviewPage>
        </>
      );
    }

    /* 결제 필요 모드 (PAYMENT_ENABLED=true) */
    return (
      <>
        <Global />
        <PreviewPage>
          <PreviewTopBar />

          <PreviewHero>
            <PreviewCharImg src="/woo_y_1.webp" alt="우연" />
            <PreviewBrandBox>
              <PreviewBrand>{t("client.brandName")}</PreviewBrand>
              <PreviewBrandSub>{t("client.brandTagline")}</PreviewBrandSub>
            </PreviewBrandBox>
          </PreviewHero>

          <PreviewBadge $color={color}>{t("client.analysisComplete", { svc: t(`svc.${preview.serviceType}`) })}</PreviewBadge>

          {/* 미리보기 카드 */}
          <PreviewCard style={{ position: "relative" }}>
            <SparkleWrap>
              {[
                { x:12, y:15, d:0,   s:0.7 },
                { x:80, y:10, d:0.8, s:0.6 },
                { x:90, y:55, d:1.5, s:0.5 },
                { x:5,  y:70, d:2.2, s:0.6 },
                { x:55, y:5,  d:1.1, s:0.5 },
              ].map((sp, i) => (
                <SparkleItem key={i} $x={sp.x} $y={sp.y} $delay={sp.d} $size={sp.s}>✦</SparkleItem>
              ))}
            </SparkleWrap>
            <PreviewCardTop />
            <PreviewCardInner>
              <PreviewAnalysisTitle>
                {freeHtml
                  ? t("client.freeResultTitle", { name: form.name })
                  : t("client.previewTeaseTitle", { name: form.name })}
              </PreviewAnalysisTitle>
              {/* 무료 구간 결과지 — '당신은 어떤 사람인가'에 해당하는 꼭지를 그대로 보여준다.
                  만들지 못했을 때만 예전 미리보기 문구로 대체된다. */}
              {freeHtml ? (
                <iframe
                  srcDoc={freeHtml}
                  title={t("client.freeResultTitle", { name: form.name })}
                  style={{
                    width: "100%", border: "none", borderRadius: 14,
                    minHeight: 640, display: "block", marginBottom: 4,
                  }}
                  onLoad={e => {
                    const el = e.currentTarget;
                    try {
                      const h = el.contentDocument?.documentElement?.scrollHeight;
                      if (h && h > 100) el.style.height = h + 40 + "px";
                    } catch { /* cross-origin 무시 */ }
                  }}
                />
              ) : (() => {
                const pv = splitPreview(preview.text);
                return (
                  <>
                    <PreviewTextBoxOuter>
                      <PreviewScrollArea>
                        <PreviewTextBox>
                          {pv.head}
                        </PreviewTextBox>
                      </PreviewScrollArea>
                      <PreviewFade />
                    </PreviewTextBoxOuter>
                    <PreviewHighlights intro={pv.intro} items={pv.items} color={color} />
                  </>
                );
              })()}
            </PreviewCardInner>

            <PreviewLockSection>
              <LockIcon><img loading="lazy" decoding="async" src="/woo_y_4.webp" alt="" style={{ width: 48, height: 48, objectFit: "contain", filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.15))" }} /></LockIcon>
              {/* 무료에서 준 것을 먼저 인정하고 시작한다 — 안 그러면 "이미 봤는데?"가 된다 */}
              <LockTitle>{preview.hookHead || t("client.lockTitle")}</LockTitle>
              {preview.hookBody ? (
                <div
                  style={{
                    fontSize: "0.92rem", lineHeight: 1.85, color: "#5a5348",
                    margin: "0 0 18px", textAlign: "left",
                    wordBreak: "keep-all", whiteSpace: "pre-line",
                  }}
                  dangerouslySetInnerHTML={{ __html: preview.hookBody }}
                />
              ) : (
                <LockSub>
                  {t("client.lockSubLine1")}<br />
                  {t("client.lockSubLine2")}
                </LockSub>
              )}

              {/* 제목만 늘어놓으면 아무것도 안 궁금하다 — 그 안에 뭐가 있는지를 같이 적는다 */}
              {lockedCards.length > 0 && (
                <div style={{ display: "grid", gap: 8, margin: "0 0 18px", textAlign: "left" }}>
                  {lockedCards.map((it, i) => (
                    <div key={i} style={{
                      display: "flex", alignItems: "flex-start", gap: 9,
                      background: "#FBF6EA", borderRadius: 13, padding: "12px 14px",
                    }}>
                      <span style={{ opacity: 0.5, fontSize: "0.85rem", lineHeight: 1.5 }}>🔒</span>
                      <div>
                        <div style={{ fontSize: "0.89rem", fontWeight: 800, color: "#2B2A26" }}>
                          {it.title}
                        </div>
                        {it.desc && (
                          <div style={{ fontSize: "0.78rem", color: "#8C8677", marginTop: 3,
                                        lineHeight: 1.6, wordBreak: "keep-all" }}>
                            {it.desc}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <SaveNotice style={{ margin: "0 0 12px", textAlign: "left" }}>
                <SaveNoticeTitle>⚠️ {t("common.saveWarnTitle")}</SaveNoticeTitle>
                <SaveNoticeBody>{t("common.saveWarnBefore")}</SaveNoticeBody>
              </SaveNotice>
              {/* 앞서 낸 금액을 빼준 경우 — 정가와 실제 낼 금액을 같이 보여준다 */}
              {(preview.credit || 0) > 0 && (
                <div style={{
                  background: "#FFF6DF", border: "1.5px solid #E8C87A",
                  borderRadius: 14, padding: "12px 14px", marginBottom: 14,
                  textAlign: "center", lineHeight: 1.6,
                }}>
                  <div style={{ fontSize: "0.78rem", fontWeight: 800, color: "#8A6A1F", marginBottom: 3 }}>
                    {t("client.creditTitle")}
                  </div>
                  <div style={{ fontSize: "0.92rem", color: "#2B2A26", fontWeight: 700 }}>
                    <span style={{ textDecoration: "line-through", opacity: 0.45 }}>
                      {(preview.originalPrice || 0).toLocaleString()}원
                    </span>
                    &nbsp;&nbsp;
                    <b style={{ color: "#B8862A", fontSize: "1.05rem" }}>{price.toLocaleString()}원</b>
                  </div>
                  <div style={{ fontSize: "0.72rem", color: "#8C8677", marginTop: 3 }}>
                    {t("client.creditNote", { credit: (preview.credit || 0).toLocaleString() })}
                  </div>
                </div>
              )}
              <PayBtn onClick={handlePayment}>
                {t("client.payBtn", { price: price.toLocaleString() })}
              </PayBtn>
              {/* 가장 비싼 상품일수록 "왜 이 값인가"가 숫자로 있어야 한다 */}
              {preview.hookVolume && (
                <div style={{ fontSize: "0.76rem", color: "#8C8677", marginTop: 8,
                              textAlign: "center", fontWeight: 700 }}>
                  {preview.hookVolume}
                </div>
              )}
              <PayInfo>
                {t("client.payInfoPre")}<strong>{form.email || t("client.yourEmail")}</strong>{t("client.payInfoPost")}<br />
                <a href="/legal/refund" target="_blank" rel="noreferrer"
                  style={{ color: "#B8862A", textDecoration: "underline" }}>
                  {t("client.refundPolicy")}
                </a> &nbsp;·&nbsp; {t("client.refundNote")}
              </PayInfo>

              {/* 사람에게 직접 듣고 싶은 분을 위한 안내 — 전화·방문으로 이어지는 통로 */}
              <div style={{
                marginTop: 14, paddingTop: 14,
                borderTop: "1px dashed #E0D8C4",
                fontSize: "0.76rem", color: "#8C8677", lineHeight: 1.7,
              }}>
                {t("client.humanConsultPre")}
                <a href="/" style={{ color: "#B8862A", fontWeight: 800, textDecoration: "underline" }}>
                  {t("client.humanConsultLink")}
                </a>
                {t("client.humanConsultPost")}
              </div>
            </PreviewLockSection>
          </PreviewCard>

          <div style={{ padding: "16px 20px 0", width: "100%", maxWidth: 520 }}>
            <ResetBtn onClick={() => {
              setPreview(null); setStep(0);
              setForm({ name: "", email: "", gender: "female", birth_date: "", birth_time: "", birth_time_unknown: false, birth_type: "solar", city: "서울", consent: false });
              setPartner(defaultPartner); setOptions([]); setError("");
            }}>← {t("client.resetBtn")}</ResetBtn>
          </div>
          {/* 아래 고정 버튼에 가려지지 않도록 자리를 비워둔다 */}
          <div style={{ height: 78 }} />
        </PreviewPage>

        {/* ── 화면 하단 고정 결제 버튼 ─────────────────────────────
            미리보기가 길어서 결제 버튼까지 내려오기 전에 대부분 빠져나간다.
            상품 이름을 그대로 달아 지금 무엇을 사는지 보이게 한다. */}
        <div style={{
          position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 60,
          background: "rgba(43,42,38,0.96)",
          borderTop: "1.5px solid rgba(251,246,234,0.18)",
          padding: "10px 14px calc(10px + env(safe-area-inset-bottom))",
        }}>
          <div style={{ maxWidth: 560, margin: "0 auto", display: "flex",
                        alignItems: "center", gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 800, color: "#FBF6EA",
                            whiteSpace: "nowrap", overflow: "hidden",
                            textOverflow: "ellipsis" }}>
                {t(`svc.${preview.serviceType}`)}
              </div>
              <div style={{ fontSize: 11, color: "rgba(251,246,234,0.6)", marginTop: 2 }}>
                {preview.credit
                  ? t("client.creditNote", { credit: (preview.credit || 0).toLocaleString() })
                  : t("client.refundNote")}
              </div>
            </div>
            <button
              onClick={handlePayment}
              style={{
                flexShrink: 0, padding: "13px 20px",
                background: color, border: "none", borderRadius: 50,
                fontSize: 14, fontWeight: 900, color: "#fff",
                cursor: "pointer", fontFamily: "inherit",
              }}>
              {t("client.payBtn", { price: price.toLocaleString() })}
            </button>
          </div>
        </div>
      </>
    );
  }

  /* ─── STEP 0: 서비스 선택(훅) 페이지 ─── */
  if (step === 0) {
    return (
      <>
        <Global />
        <DancheongBar />
        <Wrap>
          {/* 전체 씬 이미지 히어로 */}
          <HeroSceneWrap>
            <HeroSceneImg src="/woo_01.webp" alt="우연 사주" />
            <HeroSceneOverlay />
            <HeroTitleBlock>
              <HeroTitle>{t("client.heroTitle")}</HeroTitle>
              <HeroSub>{t("client.brandTagline")}</HeroSub>
            </HeroTitleBlock>
          </HeroSceneWrap>

          <HookWrap>
            <SectionLabel>
              <HomeBtn type="button" onClick={() => { window.location.href = "/"; }}>{t("client.home")}</HomeBtn>
              {t("client.sectionPickFortune")}
            </SectionLabel>
            <ServiceCardGrid>
              {SERVICE_TYPES.map((s, idx) => (
                <ServiceCard
                  key={s.key}
                  type="button"
                  $color={s.color}
                  $idx={idx}
                  onClick={() => selectService(s.key)}
                >
                  <SvcImgWrap>
                    <SvcImgBanner src={s.img} alt="" />
                    <SvcImgCaption>{t(`client.hook${SVC_SLUG[s.key]}`)}</SvcImgCaption>
                  </SvcImgWrap>
                  <SvcCardBody $color={s.color}>
                    <SvcHeadTitle>{t(`svc.${s.key}`)}</SvcHeadTitle>
                    <SvcSub>{t(`client.sub${SVC_SLUG[s.key]}`)}</SvcSub>
                    <SvcBtn $color={s.color}>{t("client.viewService", { svc: t(`svc.${s.key}`) })}</SvcBtn>
                  </SvcCardBody>
                </ServiceCard>
              ))}
            </ServiceCardGrid>
          </HookWrap>
          <SiteFooter />
        </Wrap>
      </>
    );
  }

  /* ─── STEP 1: 입력 폼 페이지 ─── */
  const svc = SERVICE_TYPES.find(s => s.key === serviceType)!;

  return (
    <>
      <Global />
      <DancheongBar />
      {/* ── 시주 추정 설문 모달 ─────────────────────────── */}
      {surveying && (
        <SurveyOverlay onClick={e => { if (e.target === e.currentTarget) closeSurvey(); }}>
          <SurveyModal>
            <div style={{fontSize:"1rem",fontWeight:800,color:"#2B2A26",marginBottom:4}}>
              {t("client.surveyTitle")}
            </div>
            <div style={{fontSize:"0.78rem",color:"#9a7d6a",marginBottom:18}}>
              {t("client.surveyDesc")}<br/>
              {surveying === "partner" ? t("client.surveyDescPartner") : ""}
            </div>

            {surveyResults.length === 0 ? (
              <>
                {surveyQs.map((q, qi) => (
                  <SurveyQBlock key={q.id}>
                    <SurveyQText>{qi + 1}. {q.question}</SurveyQText>
                    {q.choices.map((c, ci) => (
                      <SurveyChoiceLabel key={ci} $selected={surveyAns[qi] === ci}
                        onClick={() => setSurveyAns(a => { const n=[...a]; n[qi]=ci; return n; })}>
                        <input type="radio" readOnly checked={surveyAns[qi] === ci}
                          style={{marginTop:2,accentColor:"#B8862A",flexShrink:0}} />
                        {c}
                      </SurveyChoiceLabel>
                    ))}
                  </SurveyQBlock>
                ))}
                <div style={{display:"flex",gap:10,marginTop:8}}>
                  <button onClick={closeSurvey}
                    style={{flex:1,padding:"10px",border:"1px solid #2B2A26",borderRadius:10,background:"#FBF6EA",cursor:"pointer",fontSize:"0.82rem",color:"#8C8677"}}>
                    {t("client.cancel")}
                  </button>
                  <button onClick={submitSurvey}
                    disabled={surveyAns.some(a=>a<0)}
                    style={{flex:2,padding:"10px",border:"none",borderRadius:10,background:surveyAns.some(a=>a<0)?"#EAE3D2":"#B8862A",color:"#fff",cursor:surveyAns.some(a=>a<0)?"not-allowed":"pointer",fontSize:"0.82rem",fontWeight:700}}>
                    {surveyAns.some(a=>a<0) ? t("client.questionsRemaining", { count: surveyAns.filter(a=>a<0).length }) : t("client.estimateHour")}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{fontSize:"0.83rem",fontWeight:700,color:"#2B2A26",marginBottom:10}}>{t("client.surveyResultTitle")}</div>
                {surveyResults.map((r) => (
                  <SurveyResultItem key={r.rank} $rank={r.rank}>
                    <div style={{width:26,height:26,borderRadius:"50%",background:r.rank===1?"#B8862A":r.rank===2?"#9775FA":"#74C0FC",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"0.75rem",fontWeight:800,flexShrink:0}}>{r.rank}</div>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:800,fontSize:"0.88rem",color:"#2b1d10"}}>{r.name} ({r.hanja}) — {r.time_range}</div>
                      <div style={{fontSize:"0.75rem",color:"#888",marginTop:1}}>{t("client.surveyResultMeta", { ohaeng: r.ohaeng, animal: r.animal, confidence: r.confidence })}</div>
                    </div>
                    <button onClick={() => applySurveyResult(r.birth_hour)}
                      style={{padding:"6px 12px",border:"none",borderRadius:8,background:"#B8862A",color:"#fff",fontSize:"0.75rem",fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
                      {t("client.applyThisTime")}
                    </button>
                  </SurveyResultItem>
                ))}
                <div style={{display:"flex",gap:10,marginTop:10}}>
                  <button onClick={() => setSurveyResults([])}
                    style={{flex:1,padding:"9px",border:"1px solid #2B2A26",borderRadius:10,background:"#FBF6EA",cursor:"pointer",fontSize:"0.8rem",color:"#8C8677"}}>
                    {t("client.retake")}
                  </button>
                  <button onClick={closeSurvey}
                    style={{flex:1,padding:"9px",border:"1px solid #2B2A26",borderRadius:10,background:"#FBF6EA",cursor:"pointer",fontSize:"0.8rem",color:"#8C8677"}}>
                    {t("client.close")}
                  </button>
                </div>
              </>
            )}
          </SurveyModal>
        </SurveyOverlay>
      )}
      <Wrap>
        {/* Step 1 전체 씬 히어로 — 서비스별 이미지 사용 */}
        <HeroSceneWrap>
          <HeroSceneImg src={svc.img || "/woo_04.webp"} alt={svc.key} />
          <HeroSceneOverlay />
          <HeroTitleBlock>
            <HeroTitle>{svc.icon} {t(`svc.${svc.key}`)}</HeroTitle>
            <HeroSub>{t(`client.sub${SVC_SLUG[svc.key]}`)}</HeroSub>
          </HeroTitleBlock>
        </HeroSceneWrap>

        <FormWrap style={{ padding: "0 14px" }}>
          <BackBtn type="button" onClick={() => { setStep(0); setError(""); }}>
            ← {t("client.backToServices")}
          </BackBtn>

          {/* 서비스 안내 밴드 — 폼 카드 밖으로 분리(쿠키와 통일), 현재 톤 */}
          <div style={{
            background: `${svc.color}22`, border: "2px solid #2B2A26", borderRadius: 16,
            padding: "13px 16px", marginBottom: 14,
          }}>
            <div style={{ fontSize: "0.9rem", fontWeight: 900, color: svc.color, marginBottom: 2 }}>{t(`client.hook${SVC_SLUG[svc.key]}`)}</div>
            <div style={{ fontSize: "0.76rem", color: "#6B6355", fontWeight: 600 }}>{t(`client.sub${SVC_SLUG[svc.key]}`)}</div>
          </div>

          <Card>
            {/* 관계운 필수 안내 */}
            {isCompat && (
              <CompatNoticeBand>
                <CompatNoticeTitle>⚠️ {t("client.partnerRequiredNotice")}</CompatNoticeTitle>
              </CompatNoticeBand>
            )}

            {/* 개별운세 서브옵션 */}
            <SubOptWrap $visible={serviceType === "개별운세"}>
              <SubOptInner>
                <SubOptLabel>
                  {t("client.subOptLabel")}
                  {isMinor && age !== null && (
                    <span style={{ marginLeft: 8, color: "#E03131" }}>
                      {t("client.minorNotice", { age })}
                    </span>
                  )}
                </SubOptLabel>
                <ChipRow>
                  {currentOptions.map(opt => (
                    <Chip key={opt} type="button" $active={options.includes(opt)}
                      onClick={() => toggleOption(opt)}>
                      {opt}
                    </Chip>
                  ))}
                </ChipRow>
              </SubOptInner>
            </SubOptWrap>

            <FormBody onSubmit={handleSubmit}>
              <SectionTitle>{t("client.sectionBasicInfo")}</SectionTitle>
              <FieldRow>
                <Field>
                  <Label>{t("client.labelName")}</Label>
                  <Input placeholder={t("client.placeholderName")} value={form.name} onChange={set("name")} inputMode="text" autoComplete="name" />
                </Field>
                <Field style={{ maxWidth: 110 }}>
                  <Label>{t("client.labelGender")}</Label>
                  <Select value={form.gender} onChange={set("gender")}>
                    <option value="female">{t("client.optionFemale")}</option>
                    <option value="male">{t("client.optionMale")}</option>
                  </Select>
                </Field>
              </FieldRow>
              <Field style={{ marginBottom: 10 }}>
                <Label>{t("client.labelEmail")}</Label>
                <Input type="email" placeholder="example@email.com"
                  value={form.email} onChange={set("email")}
                  inputMode="email" autoComplete="email" required />
              </Field>

              <SectionTitle>{t("client.sectionBirth")}</SectionTitle>
              <FieldRow>
                <Field>
                  <Label>{t("client.labelBirthType")}</Label>
                  <Select value={form.birth_type} onChange={set("birth_type")}>
                    {BIRTH_TYPES.map(bt => <option key={bt.value} value={bt.value}>{t(`client.birthType_${bt.value}`)}</option>)}
                  </Select>
                </Field>
                <Field style={{ flex: 2 }}>
                  <Label>{t("client.labelBirthDate")}</Label>
                  <Input placeholder="1990/05/15" maxLength={10}
                    value={form.birth_date} onChange={set("birth_date")} />
                </Field>
              </FieldRow>
              <FieldRow style={{ alignItems: "center", marginBottom: 4 }}>
                <Label style={{ marginBottom: 0 }}>{t("client.labelBirthTime")}</Label>
                <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.78rem", color: "#8C8677", cursor: "pointer", marginLeft: 8 }}>
                  <input type="checkbox" checked={form.birth_time_unknown}
                    onChange={e => setForm(f => ({ ...f, birth_time_unknown: e.target.checked }))}
                    style={{ accentColor: "#B8862A" }} />
                  {t("client.timeUnknown")}
                </label>
                {form.birth_time_unknown && (
                  <button type="button" onClick={() => openSurvey("self")}
                    style={{marginLeft:"auto",padding:"4px 10px",border:"1px solid #2B2A26",borderRadius:6,background:"#fff8f0",color:"#B8862A",fontSize:"0.73rem",fontWeight:700,cursor:"pointer"}}>
                    {t("client.estimateHelp")}
                  </button>
                )}
              </FieldRow>
              <FieldRow>
                <Field>
                  <Input placeholder={t("client.placeholderTime")} maxLength={5}
                    value={form.birth_time_unknown ? "" : form.birth_time}
                    disabled={form.birth_time_unknown}
                    style={form.birth_time_unknown ? { background: "#FBF6EA", color: "#8C8677", cursor: "not-allowed" } : {}}
                    onChange={set("birth_time")} />
                </Field>
              </FieldRow>

              <SectionTitle>{t("client.sectionRegion")}</SectionTitle>
              <Field style={{ marginBottom: 0 }}>
                <CityAutocomplete
                  value={form.city}
                  onChange={v => setForm(f => ({ ...f, city: v }))}
                />
              </Field>

              {/* 관계운 파트너 폼 */}
              <PartnerWrap $visible={isCompat}>
                <PartnerDivider>
                  <PartnerTitle>
                    {t("client.partnerInfo")}
                  </PartnerTitle>
                  <PartnerSub>
                    {serviceType === "연애궁합"
                      ? t("client.partnerSubLove")
                      : t("client.partnerSubDeep")}
                  </PartnerSub>
                  <FieldRow>
                    <Field>
                      <Label>{t("client.labelPartnerName")}</Label>
                      <Input placeholder={t("client.placeholderPartnerName")} value={partner.name} onChange={setP("name")} />
                    </Field>
                    <Field>
                      <Label>{t("client.labelGender")}</Label>
                      <Select value={partner.gender} onChange={setP("gender")}>
                        <option value="female">{t("client.optionFemale")}</option>
                        <option value="male">{t("client.optionMale")}</option>
                      </Select>
                    </Field>
                  </FieldRow>
                  <FieldRow>
                    <Field>
                      <Label>{t("client.labelBirthType")}</Label>
                      <Select value={partner.birth_type} onChange={setP("birth_type")}>
                        {BIRTH_TYPES.map(bt => <option key={bt.value} value={bt.value}>{t(`client.birthType_${bt.value}`)}</option>)}
                      </Select>
                    </Field>
                    <Field style={{ flex: 2 }}>
                      <Label>{t("client.labelBirthDate")}</Label>
                      <Input placeholder="1990/05/15" maxLength={10}
                        value={partner.birth_date} onChange={setP("birth_date")} />
                    </Field>
                  </FieldRow>
                  <FieldRow style={{ alignItems: "center", marginBottom: 4 }}>
                    <Label style={{ marginBottom: 0 }}>{t("client.labelBirthTime")}</Label>
                    <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.78rem", color: "#8C8677", cursor: "pointer", marginLeft: 8 }}>
                      <input type="checkbox" checked={partner.birth_time_unknown}
                        onChange={e => setPartner(p => ({ ...p, birth_time_unknown: e.target.checked }))}
                        style={{ accentColor: "#B8862A" }} />
                      {t("client.timeUnknown")}
                    </label>
                    {partner.birth_time_unknown && (
                      <button type="button" onClick={() => openSurvey("partner")}
                        style={{marginLeft:"auto",padding:"4px 10px",border:"1px solid #2B2A26",borderRadius:6,background:"#fff8f0",color:"#B8862A",fontSize:"0.73rem",fontWeight:700,cursor:"pointer"}}>
                        {t("client.estimateHelp")}
                      </button>
                    )}
                  </FieldRow>
                  <FieldRow>
                    <Field>
                      <Input placeholder={t("client.placeholderTime")} maxLength={5}
                        value={partner.birth_time_unknown ? "" : partner.birth_time}
                        disabled={partner.birth_time_unknown}
                        style={partner.birth_time_unknown ? { background: "#FBF6EA", color: "#8C8677", cursor: "not-allowed" } : {}}
                        onChange={setP("birth_time")} />
                    </Field>
                  </FieldRow>
                  <Field style={{ marginBottom: 4 }}>
                    <Label>{t("client.labelRegion")}</Label>
                    <CityAutocomplete
                      value={partner.city}
                      onChange={v => setPartner(p => ({ ...p, city: v }))}
                    />
                  </Field>
                </PartnerDivider>
              </PartnerWrap>

              <ConsentBox>
                <input type="checkbox" checked={form.consent}
                  onChange={e => setForm(f => ({ ...f, consent: e.target.checked }))} />
                <span>
                  {t("client.consentPre")}
                  <a href="/legal/terms" target="_blank" rel="noreferrer" style={{ color: "#8C8677" }}>{t("client.linkTerms")}</a>
                  {" "}{t("client.consentAnd")}{" "}
                  <a href="/legal/privacy" target="_blank" rel="noreferrer" style={{ color: "#8C8677" }}>{t("client.linkPrivacy")}</a>
                  {t("client.consentText")}
                </span>
              </ConsentBox>
            </FormBody>

            {/* 리포트는 재발급이 안 되므로 접수 전에 미리 안내 */}
            <SaveNotice>
              <SaveNoticeTitle>⚠️ {t("common.saveWarnTitle")}</SaveNoticeTitle>
              <SaveNoticeBody>{t("common.saveWarnBefore")}</SaveNoticeBody>
            </SaveNotice>

            {error && <ErrorMsg>{error}</ErrorMsg>}

            <SubmitBtn
              type="submit"
              $disabled={loading}
              onClick={handleSubmit as any}
              disabled={loading}
            >
              {loading ? t("client.submitting") : t("client.submitService", { svc: t(`svc.${svc.key}`) })}
            </SubmitBtn>
          </Card>
        </FormWrap>

        {/* ── 사업자 정보 푸터 ── */}
        <SiteFooter />

        {/* ── 테스트용 이메일 발송 버튼 ── */}
      </Wrap>
    </>
  );
}
